import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import useDriverDniLookup from "./useDriverDniLookup";
import { toast } from "react-toastify";
import { useAuth } from "../../provider/authProvider";
import inventoryApi from "../../services/inventoryApi";

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 44,
        borderRadius: 10,
        borderColor: state.isFocused ? "#2563eb" : "#d7deea",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(37,99,235,.12)" : "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const statusText = (status) =>
    ({
        DRAFT: "Borrador",
        READY: "Lista",
        PROCESSING: "Procesando",
        PARTIAL: "Parcial",
        COMPLETED: "Completada",
        ERROR: "Con incidencia",
        CANCELLED: "Cancelada",
        PENDING: "Pendiente",
        GENERATING_XML: "Generando XML",
        SIGNED: "Firmada",
        SENT: "Enviada",
        ACCEPTED: "Aceptada",
        OBSERVED: "Aceptada con observación",
        REJECTED: "Rechazada",
    })[String(status || "").toUpperCase()] || status || "—";

const GUIDE_DATE_TIME_PATTERN =
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;
const GUIDE_DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatDateTime12h = (value) => {
    if (value === null || value === undefined || value === "") return "—";

    const raw = String(value).trim();
    const dateTimeMatch = raw.match(GUIDE_DATE_TIME_PATTERN);

    if (dateTimeMatch) {
        const [, year, month, day, hour24, minute, second] = dateTimeMatch;
        const numericHour = Number(hour24);
        const period = numericHour >= 12 ? "PM" : "AM";
        const hour12 = String(numericHour % 12 || 12).padStart(2, "0");

        return `${day}/${month}/${year} ${hour12}:${minute}:${second} ${period}`;
    }

    const dateOnlyMatch = raw.match(GUIDE_DATE_ONLY_PATTERN);

    if (dateOnlyMatch) {
        const [, year, month, day] = dateOnlyMatch;
        return `${day}/${month}/${year}`;
    }

    return raw;
};

const apiPayload = (value) => {
    if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        Object.prototype.hasOwnProperty.call(value, "data")
    ) {
        return value.data;
    }

    return value;
};

const localIsoDate = () => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Lima",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(new Date());

    const values = Object.fromEntries(
        parts
            .filter((part) => part.type !== "literal")
            .map((part) => [part.type, part.value]),
    );

    return `${values.year}-${values.month}-${values.day}`;
};

const initialForm = {
    mode: "INTERNAL",
    transferId: null,
    originLocationId: null,
    destinationLocationId: null,
    transferStartDate: localIsoDate(),
    originSedeId: null,
    originAddress: "",
    originUbigeo: "",
    originEstablishmentCode: "",
    destinationSedeId: null,
    destinationAddress: "",
    destinationUbigeo: "",
    destinationEstablishmentCode: "",
    grossWeight: "",
    packageCount: "1",
    vehiclePlate: "",
    driverDocumentType: "1",
    driverDocumentNumber: "",
    driverFirstNames: "",
    driverLastNames: "",
    driverLicense: "",
    notes: "",
    rucEstablishments: [],
};

function GuideField({ label, children, wide = false, hint = null }) {
    return (
        <label className={`shipping-guide-field ${wide ? "wide" : ""}`}>
            <span>{label}</span>
            {children}
            {hint ? <small>{hint}</small> : null}
        </label>
    );
}

export default function ShippingGuides({ rows = [], canManage = false, reload, dateFilters = {} }) {
    const auth = useAuth();
    const isAdmin = String(auth.rol || "") === "1";
    const [localRows, setLocalRows] = useState(rows || []);
    const [createOpen, setCreateOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [validation, setValidation] = useState(null);
    const [oauthOpen, setOauthOpen] = useState(false);
    const [cancellationTarget, setCancellationTarget] = useState(null);
    const [busy, setBusy] = useState(null);

    useEffect(() => setLocalRows(rows || []), [rows]);

    const refresh = async () => {
        if (reload) {
            await reload();
            return;
        }
        const next = apiPayload(
            await inventoryApi.shippingGuides({
                dateFrom: dateFilters.dateFrom,
                dateTo: dateFilters.dateTo,
            }),
        );
        setLocalRows(Array.isArray(next) ? next : []);
    };

    const openDetail = async (guide) => {
        try {
            setBusy(`detail-${guide.id}`);
            setDetail(apiPayload(await inventoryApi.shippingGuide(guide.id)));
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo consultar la guía.");
        } finally {
            setBusy(null);
        }
    };

    const preflight = async (guide) => {
        try {
            setBusy(`preflight-${guide.id}`);
            const result = apiPayload(await inventoryApi.preflightShippingGuide(guide.id));
            if (result.valid) {
                toast.success("Prevalidación GRE completada correctamente.");
            } else {
                const incidents = (result.documents || [])
                    .flatMap((doc) => Array.isArray(doc.details) && doc.details.length ? doc.details : [doc.error])
                    .filter(Boolean);
                toast.error(
                    incidents.length
                        ? `Prevalidación con incidencia: ${incidents.join(" · ")}`
                        : "La prevalidación encontró incidencias.",
                );
            }
            await refresh();
            await openDetail(guide);
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo prevalidar la GRE.");
        } finally {
            setBusy(null);
        }
    };

    const send = async (guide) => {
        if (!window.confirm(
            `Se enviarán a SUNAT todas las GRE pendientes de ${guide.guide_number}.\n\n` +
            "Los documentos aceptados no se volverán a emitir. ¿Continuar?",
        )) return;
        try {
            setBusy(`send-${guide.id}`);
            const result = apiPayload(await inventoryApi.sendShippingGuide(guide.id));
            const accepted = (result.documents || []).filter((doc) => ["ACCEPTED", "OBSERVED"].includes(doc.sunat_status)).length;
            toast.success(`Procesamiento terminado: ${accepted}/${(result.documents || []).length} GRE con CDR aceptada.`);
            setDetail(result);
            await refresh();
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo emitir la GRE.");
            await refresh();
        } finally {
            setBusy(null);
        }
    };

    const retry = async (guide) => {
        try {
            setBusy(`retry-${guide.id}`);
            const result = apiPayload(await inventoryApi.retryShippingGuide(guide.id));
            setDetail(result);
            toast.success("Se consultaron/reprocesaron únicamente las GRE pendientes.");
            await refresh();
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo reintentar la GRE.");
        } finally {
            setBusy(null);
        }
    };

    const reissue = async (guide) => {
        if (!window.confirm("SUNAT rechazó una GRE de esta guía. Se conservará el documento rechazado y se generará una NUEVA emisión con nuevo correlativo y los códigos Sede + RUC vigentes. ¿Continuar?")) return;
        try {
            setBusy(`reissue-${guide.id}`);
            const result = apiPayload(await inventoryApi.reissueShippingGuide(guide.id));
            setDetail(result);
            toast.success("Nueva emisión creada y prevalidada. Revisa el nuevo correlativo antes de Emitir.");
            await refresh();
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo crear la nueva emisión GRE.");
        } finally { setBusy(null); }
    };

    const downloadInternal = async (guide) => {
        try {
            setBusy(`pdf-${guide.id}`);
            await inventoryApi.downloadShippingGuide(guide.id, "pdf", `${guide.guide_number}.pdf`);
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo descargar la guía interna.");
        } finally {
            setBusy(null);
        }
    };

    const downloadDocument = async (doc, type) => {
        const ext = type === "pdf" ? "pdf" : type === "xml" || type === "cdr-xml" ? "xml" : "zip";
        const prefix = type.startsWith("cdr") ? "R-" : "";
        try {
            setBusy(`${type}-${doc.id}`);
            await inventoryApi.downloadShippingGuideDocument(doc.id, type, `${prefix}${doc.full_number}.${ext}`);
        } catch (error) {
            toast.error(error.response?.data?.error || "El archivo GRE todavía no está disponible.");
        } finally {
            setBusy(null);
        }
    };

    const showValidation = async (doc) => {
        try {
            setBusy(`validation-${doc.id}`);
            setValidation(apiPayload(await inventoryApi.shippingGuideValidation(doc.id)));
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo consultar la validación.");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="shipping-guides">
            <section className="shipping-guides-intro">
                <div>
                    <span>TRAZABILIDAD DOCUMENTARIA</span>
                    <h3>Guías de traslado</h3>
                    <p>
                        Centro documental de traslados. El traslado es la fuente logística de verdad; desde aquí
                        puedes documentar un traslado existente, consultar PDF y gestionar GRE SUNAT por RUC.
                    </p>
                </div>
                {/* <div className="shipping-guides-intro-actions">
                    {isAdmin && <button type="button" onClick={() => setOauthOpen(true)}>OAuth GRE</button>}
                    {canManage && <button type="button" className="primary" onClick={() => setCreateOpen(true)}>+ Documentar traslado</button>}
                </div> */}
            </section>

            <div className="shipping-guides-table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>Guía</th>
                            <th>Modalidad</th>
                            <th>Traslado</th>
                            <th>Ruta</th>
                            <th>RUC</th>
                            <th>Unidades</th>
                            <th>Estado</th>
                            <th>Documentos SUNAT</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(localRows || []).map((guide) => (
                            <tr key={guide.id}>
                                <td><strong>{guide.guide_number}</strong><small>{formatDateTime12h(guide.created_at)}</small></td>
                                <td><span className={`guide-mode ${guide.mode === "SUNAT" ? "sunat" : "internal"}`}>{guide.mode === "SUNAT" ? "GRE SUNAT" : "Interna"}</span></td>
                                <td>{guide.transfer_number}</td>
                                <td>{guide.origin_name}<small>→ {guide.destination_name}</small></td>
                                <td>{guide.ruc_count}</td>
                                <td>{Number(guide.unit_count || 0)}</td>
                                <td><span className={`guide-status status-${String(guide.status || "").toLowerCase()}`}>{statusText(guide.status)}</span></td>
                                <td className="guide-documents-cell">{guide.mode === "SUNAT" ? (guide.sunat_documents || "Pendiente") : "No aplica"}</td>
                                <td>
                                    <div className="shipping-guide-actions">
                                        <button type="button" disabled={busy === `detail-${guide.id}`} onClick={() => openDetail(guide)}>Ver</button>
                                        {guide.mode === "INTERNAL" && <button type="button" disabled={busy === `pdf-${guide.id}`} onClick={() => downloadInternal(guide)}>PDF</button>}
                                        {guide.mode === "SUNAT" && canManage && <>
                                            <button type="button" disabled={Boolean(busy)} onClick={() => preflight(guide)}>Validar</button>
                                            {!(["COMPLETED"].includes(guide.status)) && <button type="button" className="primary" disabled={Boolean(busy)} onClick={() => send(guide)}>Emitir</button>}
                                            {["PARTIAL", "PROCESSING"].includes(guide.status) && <button type="button" disabled={Boolean(busy)} onClick={() => retry(guide)}>Reintentar</button>}
                                            {guide.status === "ERROR" && <button type="button" className="guide-reissue-btn" disabled={Boolean(busy)} onClick={() => reissue(guide)}>Nueva emisión</button>}
                                            {isAdmin && ["READY", "COMPLETED", "PARTIAL", "PROCESSING", "ERROR"].includes(guide.status) && <button type="button" className="guide-cancel-btn" disabled={Boolean(busy)} onClick={() => setCancellationTarget(guide)}>Dar de baja</button>}
                                        </>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!localRows?.length && <div className="inventory-empty">Todavía no existen guías de traslado.</div>}
            </div>

            {createOpen && <CreateGuideModal close={() => setCreateOpen(false)} saved={async (guide) => {
                setCreateOpen(false);
                setDetail(guide);
                await refresh();
            }} />}
            {detail && <GuideDetailModal guide={detail} close={() => setDetail(null)} downloadDocument={downloadDocument} showValidation={showValidation} busy={busy} isAdmin={isAdmin} requestCancellation={() => setCancellationTarget(detail)} />}
            {cancellationTarget && <GreCancellationModal guide={cancellationTarget} close={() => setCancellationTarget(null)} saved={async () => { setCancellationTarget(null); setDetail(null); await refresh(); }} />}
            {validation && <ValidationModal value={validation} close={() => setValidation(null)} />}
            {oauthOpen && <GreOAuthModal close={() => setOauthOpen(false)} />}
        </div>
    );
}

function CreateGuideModal({ close, saved }) {
    const [form, setForm] = useState(initialForm);
    const [internalSource, setInternalSource] = useState("TRANSFER");
    const [locations, setLocations] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [context, setContext] = useState(null);
    const [availability, setAvailability] = useState([]);
    const [selectedUnits, setSelectedUnits] = useState([]);
    const [loadingContext, setLoadingContext] = useState(false);
    const [loadingAvailability, setLoadingAvailability] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([
            inventoryApi.meta(),
            inventoryApi.transfers(),
        ]).then(([metaResponse, transfersResponse]) => {
                const meta = apiPayload(metaResponse) || {};
                const transferRows = apiPayload(transfersResponse);
                setLocations(
                    (Array.isArray(meta.locations) ? meta.locations : []).filter(
                        (row) =>
                            ["CENTRAL_WAREHOUSE", "BRANCH"].includes(row.type) &&
                            Number(row.is_active ?? 1) !== 0,
                    ),
                );
                setTransfers(
                    (Array.isArray(transferRows) ? transferRows : []).filter(
                        (row) => !["CANCELLED", "REJECTED"].includes(row.status),
                    ),
                );
            })
            .catch((error) =>
                toast.error(
                    error.response?.data?.error ||
                        "No se pudo cargar la información necesaria para crear la guía.",
                ),
            );
    }, []);

    const transferOptions = useMemo(
        () =>
            transfers.map((row) => ({
                value: Number(row.id),
                label: `${row.transfer_number} · ${row.originName} → ${row.destinationName} · ${statusText(row.status)}`,
                raw: row,
            })),
        [transfers],
    );

    const locationOptions = useMemo(
        () =>
            locations.map((row) => ({
                value: Number(row.id),
                label: `${row.name}${row.city ? ` · ${row.city}` : ""}${row.sedeBranchCode ? ` · Est. ${row.sedeBranchCode}` : ""}`,
                raw: row,
            })),
        [locations],
    );

    const sedeFieldsFromLocation = (location) => ({
        sedeId: Number(location?.sede_id || 0) || null,
        address: location?.sedeAddress || "",
        ubigeo: location?.sedeUbigeo || "",
        establishmentCode: location?.sedeBranchCode || "",
    });

    const validateLocationSede = (location, label) => {
        if (!location) return `${label}: selecciona una ubicación.`;
        if (!Number(location.sede_id || 0)) {
            return `${label}: la ubicación “${location.name}” no está vinculada a una sede.`;
        }
        if (!String(location.sedeAddress || "").trim()) {
            return `${label}: la sede vinculada no tiene dirección configurada.`;
        }
        if (!/^\d{6}$/.test(String(location.sedeUbigeo || ""))) {
            return `${label}: la sede vinculada no tiene UBIGEO válido.`;
        }
        if (!/^\d{4}$/.test(String(location.sedeBranchCode || ""))) {
            return `${label}: la sede vinculada no tiene código de establecimiento de 4 dígitos.`;
        }
        return null;
    };

    const unitOptions = useMemo(
        () =>
            (availability || []).flatMap((product) => {
                if (!product.isSerialized) return [];
                return (product.serials || []).map((serial) => {
                    const imei = serial.imei1 || serial.serialNumber || serial.imei2 || `ID ${serial.serialId}`;
                    const issuerName = serial.issuerName || product.issuerName || "RUC no identificado";
                    const issuerRuc = serial.issuerRuc || product.issuerRuc || "—";
                    return {
                        value: Number(serial.serialId),
                        label: `${product.title} · IMEI ${imei} · ${issuerName}`,
                        raw: {
                            ...serial,
                            productId: Number(product.productId),
                            variantId: product.variantId ?? null,
                            title: product.title,
                            sku: product.sku,
                            unitCost: Number(product.unitCost || serial.purchaseCost || 0),
                            issuerRucId: Number(serial.issuerRucId || product.issuerRucId || 0) || null,
                            issuerRuc,
                            issuerName,
                        },
                    };
                });
            }),
        [availability],
    );

    const selectedUnitOptions = useMemo(() => {
        const selectedIds = new Set(selectedUnits.map((unit) => Number(unit.serialId)));
        return unitOptions.filter((option) => selectedIds.has(Number(option.value)));
    }, [selectedUnits, unitOptions]);

    const batchGroups = useMemo(() => {
        const groups = new Map();
        for (const unit of selectedUnits) {
            const rucId = Number(unit.issuerRucId || 0);
            const key = rucId || `NO-RUC-${unit.productId}`;
            if (!groups.has(key)) {
                groups.set(key, {
                    issuerRucId: rucId || null,
                    businessName: unit.issuerName || "RUC no identificado",
                    ruc: unit.issuerRuc || "—",
                    unitCount: 0,
                });
            }
            groups.get(key).unitCount += 1;
        }
        return [...groups.values()];
    }, [selectedUnits]);

    const selectedProductCount = useMemo(
        () => new Set(selectedUnits.map((unit) => `${unit.productId}:${unit.variantId ?? "null"}`)).size,
        [selectedUnits],
    );

    const driverDni = useDriverDniLookup(useCallback((dni, first, last) => {
        setForm(current => current.driverDocumentNumber === dni ? {...current, driverFirstNames:first, driverLastNames:last} : current);
    }, []));
    const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

    const setRucEstablishment = (issuerRucId, key, value) => {
        const normalized = String(value || "").replace(/\D/g, "").slice(0, 4);
        setForm((current) => ({
            ...current,
            rucEstablishments: (current.rucEstablishments || []).map((row) =>
                Number(row.issuerRucId) === Number(issuerRucId)
                    ? { ...row, [key]: normalized }
                    : row,
            ),
        }));
    };

    const resetBatch = () => {
        setAvailability([]);
        setSelectedUnits([]);
        setContext(null);
        setForm((current) => ({
            ...current,
            transferId: null,
            originLocationId: null,
            destinationLocationId: null,
            originSedeId: null,
            destinationSedeId: null,
            originAddress: "",
            originUbigeo: "",
            originEstablishmentCode: "",
            destinationAddress: "",
            destinationUbigeo: "",
            destinationEstablishmentCode: "",
            rucEstablishments: [],
        }));
    };

    const changeMode = (mode) => {
        setForm((current) => ({
            ...current,
            mode,
            transferId: null,
            rucEstablishments: [],
        }));
        setContext(null);
        setAvailability([]);
        setSelectedUnits([]);
        if (mode === "SUNAT") setInternalSource("TRANSFER");
    };

    const chooseTransfer = async (item) => {
        const transferId = item?.value || null;
        set("transferId", transferId);
        setContext(null);
        if (!transferId) return;
        setLoadingContext(true);
        try {
            const data = apiPayload(await inventoryApi.shippingGuideContext(transferId));
            setContext(data);
            setForm((current) => ({
                ...current,
                transferId,
                transferStartDate: data.suggested?.transferStartDate || current.transferStartDate,
                originSedeId: data.suggested?.originSedeId || null,
                originAddress: data.suggested?.originAddress || "",
                originUbigeo: data.suggested?.originUbigeo || "",
                originEstablishmentCode: data.suggested?.originEstablishmentCode || "",
                destinationSedeId: data.suggested?.destinationSedeId || null,
                destinationAddress: data.suggested?.destinationAddress || "",
                destinationUbigeo: data.suggested?.destinationUbigeo || "",
                destinationEstablishmentCode: data.suggested?.destinationEstablishmentCode || "",
                rucEstablishments: (data.groups || []).map((group) => ({
                    issuerRucId: Number(group.issuerRucId),
                    originEstablishmentCode: data.suggested?.originEstablishmentCode || "",
                    destinationEstablishmentCode: data.suggested?.destinationEstablishmentCode || "",
                })),
            }));
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo preparar el traslado para la guía.");
        } finally {
            setLoadingContext(false);
        }
    };

    const loadFreshAvailability = async (originLocationId) => {
        if (!originLocationId) {
            setAvailability([]);
            return [];
        }

        const data = apiPayload(
            await inventoryApi.transferAvailability(Number(originLocationId)),
        );
        const products = Array.isArray(data?.products) ? data.products : [];

        setAvailability(products);

        return products;
    };

    const getAvailableSerialIds = (products) =>
        new Set(
            (products || []).flatMap((product) =>
                (product.isSerialized ? product.serials || [] : []).map((serial) =>
                    Number(serial.serialId),
                ),
            ),
        );

    const chooseOrigin = async (item) => {
        const originLocationId = item?.value || null;
        const location = item?.raw || null;
        const sede = sedeFieldsFromLocation(location);

        setAvailability([]);
        setSelectedUnits([]);
        setForm((current) => {
            const sameDestination =
                Number(current.destinationLocationId) === Number(originLocationId);

            return {
                ...current,
                originLocationId,
                originSedeId: sede.sedeId,
                originAddress: sede.address,
                originUbigeo: sede.ubigeo,
                originEstablishmentCode: sede.establishmentCode,
                destinationLocationId: sameDestination
                    ? null
                    : current.destinationLocationId,
                ...(sameDestination
                    ? {
                        destinationSedeId: null,
                        destinationAddress: "",
                        destinationUbigeo: "",
                        destinationEstablishmentCode: "",
                    }
                    : {}),
            };
        });

        if (!originLocationId) return;

        const sedeError = validateLocationSede(location, "Origen");
        if (sedeError) toast.error(sedeError);

        setLoadingAvailability(true);
        try {
            const products = await loadFreshAvailability(originLocationId);
            const serializedCount = products.reduce(
                (total, product) => total + (product.isSerialized ? (product.serials || []).length : 0),
                0,
            );
            if (!serializedCount) {
                toast.info("No hay equipos serializados/IMEI disponibles en el origen seleccionado.");
            }
        } catch (error) {
            setAvailability([]);
            toast.error(
                error.response?.data?.error ||
                    "No se pudo consultar los equipos disponibles del origen.",
            );
        } finally {
            setLoadingAvailability(false);
        }
    };

    const chooseUnits = (options) => {
        setSelectedUnits((options || []).map((option) => option.raw));
    };

    const selectAllUnits = () => setSelectedUnits(unitOptions.map((option) => option.raw));
    const clearUnits = () => setSelectedUnits([]);

    const buildTransferItems = () => {
        const grouped = new Map();
        for (const unit of selectedUnits) {
            const key = `${Number(unit.productId)}:${unit.variantId ?? "null"}`;
            if (!grouped.has(key)) {
                grouped.set(key, {
                    productId: Number(unit.productId),
                    variantId: unit.variantId === null || unit.variantId === undefined
                        ? null
                        : Number(unit.variantId),
                    quantity: 0,
                    unitCost: Number(unit.unitCost || 0),
                    serialIds: [],
                });
            }
            const item = grouped.get(key);
            item.serialIds.push(Number(unit.serialId));
            item.quantity = item.serialIds.length;
        }
        return [...grouped.values()];
    };

    const createInternalFromBatch = async () => {
        if (!form.originLocationId || !form.destinationLocationId) {
            throw new Error("Selecciona el origen y el destino de la guía interna.");
        }
        if (Number(form.originLocationId) === Number(form.destinationLocationId)) {
            throw new Error("El origen y el destino deben ser diferentes.");
        }

        const originLocation = locations.find(
            (row) => Number(row.id) === Number(form.originLocationId),
        );
        const destinationLocation = locations.find(
            (row) => Number(row.id) === Number(form.destinationLocationId),
        );
        const originError = validateLocationSede(originLocation, "Origen");
        const destinationError = validateLocationSede(destinationLocation, "Destino");

        if (originError) throw new Error(originError);
        if (destinationError) throw new Error(destinationError);
        if (!selectedUnits.length) {
            throw new Error("Selecciona al menos un equipo/IMEI para crear la guía interna.");
        }
        const missingRuc = selectedUnits.find((unit) => !Number(unit.issuerRucId || 0));
        if (missingRuc) {
            throw new Error(
                `El equipo ${missingRuc.imei1 || missingRuc.serialNumber || missingRuc.serialId} no tiene RUC asociado. Corrige el equipo antes de crear la guía.`,
            );
        }

        // Revalidación final contra backend justo antes de crear el traslado.
        // El modal puede permanecer abierto mientras otra operación vende,
        // reserva o mueve un IMEI; nunca se confía en una selección antigua.
        const freshProducts = await loadFreshAvailability(form.originLocationId);
        const freshSerialIds = getAvailableSerialIds(freshProducts);

        const stillAvailableUnits = selectedUnits.filter((unit) =>
            freshSerialIds.has(Number(unit.serialId)),
        );
        const unavailableUnits = selectedUnits.filter(
            (unit) => !freshSerialIds.has(Number(unit.serialId)),
        );

        if (unavailableUnits.length) {
            setSelectedUnits(stillAvailableUnits);

            const detail = unavailableUnits
                .slice(0, 3)
                .map(
                    (unit) =>
                        unit.imei1 ||
                        unit.serialNumber ||
                        `ID ${unit.serialId}`,
                )
                .join(", ");

            throw new Error(
                `${unavailableUnits.length} equipo(s) dejaron de estar disponibles y fueron retirados de la selección${detail ? `: ${detail}` : ""}. La lista ya fue actualizada; revisa los equipos restantes y vuelve a crear la guía.`,
            );
        }

        const transferResponse = apiPayload(
            await inventoryApi.createTransfer({
                originLocationId: Number(form.originLocationId),
                destinationLocationId: Number(form.destinationLocationId),
                notes: form.notes || "Traslado generado desde Guía Interna",
                items: buildTransferItems(),
            }),
        );
        const transferId = Number(transferResponse?.id || 0);
        if (!transferId) throw new Error("El traslado fue creado, pero el backend no devolvió su ID.");

        try {
            return apiPayload(
                await inventoryApi.createShippingGuide({
                    ...form,
                    mode: "INTERNAL",
                    transferId,
                }),
            );
        } catch (error) {
            const message =
                error.response?.data?.error || error.message || "No se pudo crear la guía interna.";
            const transferNumber = transferResponse?.transferNumber || `ID ${transferId}`;
            throw new Error(
                `El traslado ${transferNumber} sí fue creado con ${selectedUnits.length} equipo(s), pero la guía interna no pudo generarse: ${message}. Puedes reutilizar ese traslado desde “Usar traslado existente”.`,
            );
        }
    };

    const submit = async () => {
        const sunat = form.mode === "SUNAT";
        if (!sunat && internalSource === "BATCH") {
            setSaving(true);
            try {
                const guide = await createInternalFromBatch();
                toast.success(
                    `Guía interna ${guide.guide_number} creada con ${selectedUnits.length} equipo(s) en un solo documento.`,
                );
                saved(guide);
            } catch (error) {
                toast.error(error.response?.data?.error || error.message || "No se pudo crear la guía interna.");
            } finally {
                setSaving(false);
            }
            return;
        }

        if (!form.transferId) return toast.error("Selecciona un traslado.");
        if (!form.originSedeId || !form.destinationSedeId) {
            return toast.error(
                "El origen o destino del traslado no está vinculado correctamente a una sede.",
            );
        }
        if (Number(form.originSedeId) === Number(form.destinationSedeId)) {
            return toast.error("La sede de partida y la sede de llegada deben ser diferentes.");
        }
        if (
            !/^\d{6}$/.test(String(form.originUbigeo || "")) ||
            !/^\d{4}$/.test(String(form.originEstablishmentCode || ""))
        ) {
            return toast.error("La sede de partida no tiene UBIGEO o código de establecimiento válido.");
        }
        if (
            !/^\d{6}$/.test(String(form.destinationUbigeo || "")) ||
            !/^\d{4}$/.test(String(form.destinationEstablishmentCode || ""))
        ) {
            return toast.error("La sede de llegada no tiene UBIGEO o código de establecimiento válido.");
        }
        if (sunat && context?.transfer && context.transfer.status !== "APPROVED") {
            return toast.error(
                "Para GRE SUNAT el traslado debe estar en estado APROBADO y todavía no despachado.",
            );
        }

        setSaving(true);
        try {
            const payload = {
                ...form,
                rucEstablishments: (form.rucEstablishments || []).map((row) => ({
                    ...row,
                    originEstablishmentCode: form.originEstablishmentCode,
                    destinationEstablishmentCode: form.destinationEstablishmentCode,
                })),
            };
            const documentationResult = apiPayload(
                await inventoryApi.documentTransfer(form.transferId, {
                    mode: form.mode,
                    documentation: payload,
                }),
            );
            const guide = documentationResult?.guide || documentationResult;
            if (!guide?.guide_number) {
                toast.success(
                    documentationResult?.message ||
                        "La documentación quedó solicitada para el traslado.",
                );
                return;
            }
            toast.success(
                sunat
                    ? `Grupo GRE ${guide.guide_number} preparado por RUC.`
                    : `Guía interna ${guide.guide_number} creada con todos los equipos del traslado.`,
            );
            saved(guide);
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo crear la guía.");
        } finally {
            setSaving(false);
        }
    };

    const sunat = form.mode === "SUNAT";
    const useBatch = false;
    const originOption = locationOptions.find(
        (item) => item.value === Number(form.originLocationId),
    ) || null;
    const destinationOption = locationOptions.find(
        (item) => item.value === Number(form.destinationLocationId),
    ) || null;

    return (
        <div className="inventory-modal shipping-guide-modal">
            <div className="backdrop" onClick={close} />
            <section>
                <header>
                    <div><small>CENTRO DOCUMENTAL</small><h2>Documentar traslado existente</h2></div>
                    <button type="button" onClick={close}>×</button>
                </header>
                <div className="shipping-guide-form">
                    <GuideField wide label="Modalidad">
                        <div className="guide-mode-picker">
                            <button type="button" className={form.mode === "INTERNAL" ? "active" : ""} onClick={() => changeMode("INTERNAL")}><strong>Guía interna</strong><small>1 reporte · múltiples equipos · múltiples RUC · no SUNAT</small></button>
                            <button type="button" className={form.mode === "SUNAT" ? "active sunat" : ""} onClick={() => changeMode("SUNAT")}><strong>GRE auténtica</strong><small>1 GRE por RUC · envío SUNAT</small></button>
                        </div>
                    </GuideField>

                    {false && !sunat && (
                        <GuideField wide label="Cómo deseas armar la guía interna">
                            <div className="guide-source-picker">
                                <button
                                    type="button"
                                    className={internalSource === "BATCH" ? "active" : ""}
                                    onClick={() => {
                                        setInternalSource("BATCH");
                                        resetBatch();
                                    }}
                                >
                                    <strong>Seleccionar equipos</strong>
                                    <small>Elige 1, 10, 50 o más IMEI y el sistema crea un solo traslado + una sola guía.</small>
                                </button>
                                <button
                                    type="button"
                                    className={internalSource === "TRANSFER" ? "active" : ""}
                                    onClick={() => {
                                        setInternalSource("TRANSFER");
                                        setAvailability([]);
                                        setSelectedUnits([]);
                                    }}
                                >
                                    <strong>Usar traslado existente</strong>
                                    <small>Conserva el flujo anterior para documentar un traslado ya creado.</small>
                                </button>
                            </div>
                        </GuideField>
                    )}

                    {useBatch ? (
                        <>
                            <GuideField label="Origen">
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    options={locationOptions}
                                    value={originOption}
                                    onChange={chooseOrigin}
                                    isLoading={loadingAvailability}
                                    placeholder="Selecciona la sede/almacén de origen"
                                />
                            </GuideField>
                            <GuideField label="Destino">
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    options={locationOptions.filter(
                                        (item) => item.value !== Number(form.originLocationId),
                                    )}
                                    value={destinationOption}
                                    onChange={(item) => {
                                        const location = item?.raw || null;
                                        const sede = sedeFieldsFromLocation(location);
                                        setForm((current) => ({
                                            ...current,
                                            destinationLocationId: item?.value || null,
                                            destinationSedeId: sede.sedeId,
                                            destinationAddress: sede.address,
                                            destinationUbigeo: sede.ubigeo,
                                            destinationEstablishmentCode: sede.establishmentCode,
                                        }));

                                        if (location) {
                                            const sedeError = validateLocationSede(
                                                location,
                                                "Destino",
                                            );
                                            if (sedeError) toast.error(sedeError);
                                        }
                                    }}
                                    isDisabled={!form.originLocationId}
                                    placeholder="Selecciona el destino"
                                />
                            </GuideField>
                            <GuideField
                                wide
                                label="Equipos / IMEI a trasladar"
                                hint="Solo aparecen IMEI respaldados por stock real, disponibles físicamente en el origen y sin otro traslado activo. La selección se revalida antes de crear la guía."
                            >
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    options={unitOptions}
                                    value={selectedUnitOptions}
                                    onChange={chooseUnits}
                                    isDisabled={!form.originLocationId || loadingAvailability}
                                    isLoading={loadingAvailability}
                                    placeholder={
                                        !form.originLocationId
                                            ? "Selecciona primero el origen"
                                            : "Busca por producto, IMEI o RUC y selecciona varios equipos"
                                    }
                                    noOptionsMessage={() => "No hay equipos/IMEI disponibles en este origen"}
                                />
                                <div className="guide-batch-actions">
                                    <div>
                                        <strong>{selectedUnits.length}</strong>
                                        <span> equipo(s) seleccionado(s) de {unitOptions.length} disponibles</span>
                                        <small>{selectedProductCount} producto(s) · {batchGroups.length} RUC</small>
                                    </div>
                                    <div>
                                        <button type="button" onClick={selectAllUnits} disabled={!unitOptions.length}>Seleccionar todos</button>
                                        <button type="button" onClick={clearUnits} disabled={!selectedUnits.length}>Limpiar</button>
                                    </div>
                                </div>
                            </GuideField>

                            {selectedUnits.length > 0 && (
                                <GuideField wide label="Resumen de la guía interna">
                                    <div className="guide-batch-summary">
                                        <div className="guide-batch-kpis">
                                            <article><span>Equipos</span><strong>{selectedUnits.length}</strong></article>
                                            <article><span>Productos</span><strong>{selectedProductCount}</strong></article>
                                            <article><span>RUC</span><strong>{batchGroups.length}</strong></article>
                                            <article><span>Documentos</span><strong>1 guía interna</strong></article>
                                        </div>
                                        <div className="guide-ruc-summary">
                                            {batchGroups.map((group, index) => (
                                                <article key={`${group.issuerRucId || "no-ruc"}-${index}`}>
                                                    <div><strong>{group.businessName}</strong><span>{group.ruc}</span></div>
                                                    <b>{group.unitCount} unidad(es)</b>
                                                </article>
                                            ))}
                                        </div>
                                        <p>
                                            Al guardar, el sistema crea <b>un solo traslado</b> con todos los IMEI seleccionados y, sobre ese traslado, <b>una sola Guía Interna</b>. No se crea una guía por equipo.
                                        </p>
                                    </div>
                                </GuideField>
                            )}

                            {(availability || []).some((product) => !product.isSerialized) && (
                                <div className="shipping-guide-warning wide">
                                    <strong>Productos sin IMEI:</strong> la selección masiva directa está orientada a equipos serializados. Para productos controlados por cantidad puedes usar “Usar traslado existente”, conservando el flujo actual.
                                </div>
                            )}
                        </>
                    ) : (
                        <GuideField wide label={sunat ? "Traslado aprobado" : "Traslado a documentar"}>
                            <Select
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                options={sunat ? transferOptions.filter((item) => item.raw.status === "APPROVED") : transferOptions}
                                value={transferOptions.find((item) => item.value === Number(form.transferId)) || null}
                                onChange={chooseTransfer}
                                isLoading={loadingContext}
                                placeholder={sunat ? "Selecciona el traslado APROBADO para preparar GRE" : "Selecciona el traslado que deseas documentar"}
                            />
                        </GuideField>
                    )}

                    {!useBatch && context && (
                        <GuideField wide label="Distribución automática por RUC">
                            <div className="guide-ruc-summary">
                                {(context.groups || []).map((group) => {
                                    return <article key={group.issuerRucId}>
                                        <div><strong>{group.businessName}</strong><span>{group.ruc}</span></div>
                                        <b>{group.unitCount} unidad(es)</b>
                                        {sunat && <>
                                            <small className={group.hasGreOAuth && group.hasSol && group.hasCertificate ? "ok" : "missing"}>{group.hasGreOAuth && group.hasSol && group.hasCertificate ? "Configuración lista" : "Configuración SUNAT/OAuth incompleta"}</small>
                                            <div className="guide-ruc-establishments">
                                                <label>
                                                    <span>Estab. partida</span>
                                                    <input
                                                        readOnly
                                                        value={form.originEstablishmentCode || ""}
                                                    />
                                                </label>
                                                <label>
                                                    <span>Estab. llegada</span>
                                                    <input
                                                        readOnly
                                                        value={form.destinationEstablishmentCode || ""}
                                                    />
                                                </label>
                                            </div>
                                        </>}
                                    </article>;
                                })}
                            </div>
                        </GuideField>
                    )}

                    <GuideField label="Fecha inicio traslado"><input type="date" value={form.transferStartDate} onChange={(e) => set("transferStartDate", e.target.value)} /></GuideField>
                    <GuideField label="Peso bruto (KGM)" hint={sunat ? "Obligatorio para GRE SUNAT." : "Opcional en control interno."}><input type="number" min="0" step="0.001" value={form.grossWeight} onChange={(e) => set("grossWeight", e.target.value)} placeholder="13.000" /></GuideField>
                    <GuideField label="Bultos / pallets"><input type="number" min="0" step="1" value={form.packageCount} onChange={(e) => set("packageCount", e.target.value)} /></GuideField>
                    <GuideField label="Placa vehículo"><input maxLength={8} value={form.vehiclePlate} onChange={(e) => set("vehiclePlate", e.target.value.toUpperCase())} placeholder="ABC123" /></GuideField>

                    <GuideField
                        wide
                        label="Dirección de partida"
                        hint="Se completa automáticamente según el Origen seleccionado."
                    >
                        <input
                            disabled
                            value={form.originAddress}
                            placeholder="Selecciona un origen"
                        />
                    </GuideField>
                    <GuideField label="Ubigeo partida">
                        <input disabled value={form.originUbigeo} placeholder="------" />
                    </GuideField>
                    <GuideField label="Código establecimiento partida">
                        <input disabled value={form.originEstablishmentCode} placeholder="----" />
                    </GuideField>

                    <GuideField
                        wide
                        label="Dirección de llegada"
                        hint="Se completa automáticamente según el Destino seleccionado."
                    >
                        <input
                            disabled
                            value={form.destinationAddress}
                            placeholder="Selecciona un destino"
                        />
                    </GuideField>
                    <GuideField label="Ubigeo llegada">
                        <input disabled value={form.destinationUbigeo} placeholder="------" />
                    </GuideField>
                    <GuideField label="Código establecimiento llegada">
                        <input disabled value={form.destinationEstablishmentCode} placeholder="----" />
                    </GuideField>

                    <GuideField label="DNI conductor"><input inputMode="numeric" maxLength={8} value={form.driverDocumentNumber} onChange={(e) => {const dni=e.target.value.replace(/\D/g, "").slice(0,8);driverDni.invalidate();setForm(current=>({...current,driverDocumentNumber:dni,driverFirstNames:"",driverLastNames:""}));if(dni.length===8)driverDni.lookup(dni);}} onBlur={()=>{if(form.driverDocumentNumber.length===8 && !form.driverFirstNames)driverDni.lookup(form.driverDocumentNumber);}} />{driverDni.loading && <small>Consultando DNI…</small>}{driverDni.message && <small role="status">{driverDni.message}</small>}</GuideField>
                    <GuideField label="Licencia"><input maxLength={10} value={form.driverLicense} onChange={(e) => set("driverLicense", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} /></GuideField>
                    <GuideField label="Nombres conductor"><input maxLength={250} value={form.driverFirstNames} onChange={(e) => set("driverFirstNames", e.target.value)} /></GuideField>
                    <GuideField label="Apellidos conductor"><input maxLength={250} value={form.driverLastNames} onChange={(e) => set("driverLastNames", e.target.value)} /></GuideField>
                    <GuideField wide label="Observaciones"><textarea maxLength={1000} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Observación logística opcional" /></GuideField>
                    {sunat && <div className="shipping-guide-warning wide"><strong>GRE SUNAT:</strong> el sistema generará un número T### independiente por cada RUC encontrado y tomará dirección, UBIGEO y código de establecimiento desde las sedes vinculadas al Origen y Destino. La guía no mueve el stock; el despacho continúa usando el flujo normal del traslado.</div>}
                </div>
                <footer className="inventory-modal-footer">
                    <button type="button" className="modal-button modal-button-secondary" onClick={close}>Cancelar</button>
                    <button type="button" className="modal-button modal-button-primary" disabled={saving || loadingContext || loadingAvailability} onClick={submit}>
                        {saving ? "Procesando..." : sunat ? "Preparar GRE por RUC" : useBatch ? `Crear guía con ${selectedUnits.length} equipo(s)` : "Crear guía interna"}
                    </button>
                </footer>
            </section>
        </div>
    );
}

function GuideDetailModal({ guide, close, downloadDocument, showValidation, busy, isAdmin, requestCancellation }) {
    return (
        <div className="inventory-modal shipping-guide-detail-modal">
            <div className="backdrop" onClick={close} />
            <section>
                <header><div><small>{guide.mode === "SUNAT" ? "GRE REMITENTE" : "CONTROL INTERNO"}</small><h2>{guide.guide_number}</h2></div><button type="button" onClick={close}>×</button></header>
                <div className="shipping-guide-detail">
                    <div className="guide-detail-kpis">
                        <article><span>Traslado</span><strong>{guide.transfer_number}</strong></article>
                        <article><span>Ruta</span><strong>{guide.origin_name} → {guide.destination_name}</strong></article>
                        <article><span>Estado</span><strong>{statusText(guide.status)}</strong></article>
                        <article><span>Inicio</span><strong>{formatDateTime12h(guide.transfer_start_date)}</strong></article>
                    </div>
                    {guide.mode === "SUNAT" && <section className="guide-document-list">
                        <h3>Documentos tributarios generados por RUC</h3>
                        {(guide.documents || []).map((doc) => <article key={doc.id}>
                            <div className="guide-document-main"><strong>{doc.full_number}</strong><span>{doc.issuer_name} · RUC {doc.issuer_ruc}</span></div>
                            <span className={`guide-status status-${String(doc.sunat_status || "").toLowerCase()}`}>{statusText(doc.sunat_status)}</span>
                            <div className="shipping-guide-actions">
                                <button type="button" disabled={Boolean(busy)} onClick={() => showValidation(doc)}>Validación</button>
                                {["ACCEPTED", "OBSERVED"].includes(doc.sunat_status) && <button type="button" onClick={() => downloadDocument(doc, "pdf")}>PDF</button>}
                                {doc.xml_path && <button type="button" onClick={() => downloadDocument(doc, "xml")}>XML</button>}
                                {doc.zip_path && <button type="button" onClick={() => downloadDocument(doc, "zip")}>ZIP</button>}
                                {doc.cdr_path && <><button type="button" onClick={() => downloadDocument(doc, "cdr")}>CDR</button><button type="button" onClick={() => downloadDocument(doc, "cdr-xml")}>CDR XML</button></>}
                            </div>
                            {doc.sunat_response_description && <p>{doc.sunat_response_description}</p>}
                        </article>)}
                    </section>}
                    <section className="guide-items-preview">
                        <h3>Bienes vinculados</h3>
                        <div className="guide-items-grid">
                            {(guide.items || []).map((item, index) => {
                                const serialText = (item.serials || [])
                                    .map((serial) => serial.imei_1 || serial.serial_number)
                                    .filter(Boolean)
                                    .join(", ");

                                return (
                                    <div className="guide-item-row" key={item.id}>
                                        <div className="guide-item-number" aria-label={`Bien ${index + 1}`}>
                                            {index + 1}
                                        </div>
                                        <div className="guide-item-product">
                                            <strong>{item.description}</strong>
                                        </div>
                                        <div className="guide-item-issuer">
                                            <span>{item.issuer_name}</span>
                                            <small>RUC {item.issuer_ruc}</small>
                                        </div>
                                        <div className="guide-item-serial">
                                            <span>{serialText || "Sin IMEI / serie"}</span>
                                            <small>Cant. {Number(item.quantity)}</small>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </div>
                <footer className="inventory-modal-footer">
                    {isAdmin && guide.mode === "SUNAT" && ["READY", "COMPLETED", "PARTIAL", "PROCESSING", "ERROR"].includes(guide.status) && <button type="button" className="modal-button guide-cancel-btn" onClick={requestCancellation}>Dar de baja GRE</button>}
                    <button type="button" className="modal-button modal-button-secondary" onClick={close}>Cerrar</button>
                </footer>
            </section>
        </div>
    );
}

function GreCancellationModal({ guide, close, saved }) {
    const [step, setStep] = useState("REQUEST");
    const [reason, setReason] = useState("");
    const [sunatReference, setSunatReference] = useState("");
    const [confirmationNote, setConfirmationNote] = useState("");
    const [incidentNote, setIncidentNote] = useState("");
    const [physicalMovementConfirmed, setPhysicalMovementConfirmed] = useState(false);
    const [saving, setSaving] = useState(false);

    const request = async () => {
        if (reason.trim().length < 10) return toast.error("Indica un motivo de al menos 10 caracteres.");
        try {
            setSaving(true);
            await inventoryApi.requestShippingGuideCancellation(guide.id, { reason: reason.trim() });
            setStep("CONFIRM");
            toast.success("Solicitud registrada. Ahora realiza la baja en SUNAT SOL y registra la constancia.");
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo registrar la solicitud de baja.");
        } finally { setSaving(false); }
    };

    const reverseWithoutPhysicalMovement = async () => {
        if (incidentNote.trim().length < 10) return toast.error("Describe la incidencia con al menos 10 caracteres.");
        if (!physicalMovementConfirmed) return toast.error("Confirma que la mercadería nunca salió físicamente del almacén de origen.");
        if (!window.confirm("Esta operación cancelará SOLO el traslado interno y liberará el stock reservado en el almacén de origen. La GRE aceptada, XML, ZIP y CDR NO serán eliminados y la baja SUNAT seguirá pendiente. ¿Continuar?")) return;
        try {
            setSaving(true);
            const result = await inventoryApi.reverseShippingGuideWithoutPhysicalMovement(guide.id, {
                incidentNote: incidentNote.trim(),
                physicalMovementConfirmed: true,
            });
            toast.success(result?.message || "Traslado revertido sin movimiento físico. El stock quedó liberado en el origen.");
            await saved();
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo ejecutar la reversión logística.");
        } finally { setSaving(false); }
    };

    const confirm = async () => {
        if (sunatReference.trim().length < 4) return toast.error("Registra la constancia o referencia de SUNAT.");
        if (!window.confirm("Esta acción cancelará el traslado interno. Los XML, ZIP y CDR se conservarán como evidencia. ¿Confirmar la baja?")) return;
        try {
            setSaving(true);
            await inventoryApi.confirmShippingGuideCancellation(guide.id, {
                sunatReference: sunatReference.trim(), confirmationNote: confirmationNote.trim(),
            });
            toast.success("Baja confirmada y traslado cancelado sin eliminar la trazabilidad SUNAT.");
            await saved();
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo confirmar la baja.");
        } finally { setSaving(false); }
    };

    return <div className="inventory-modal shipping-guide-cancellation-modal">
        <div className="backdrop" onClick={saving ? undefined : close} />
        <section>
            <header><div><small>CONTROL ADMINISTRATIVO SUNAT</small><h2>Dar de baja GRE</h2></div><button type="button" disabled={saving} onClick={close}>×</button></header>
            <div className="shipping-guide-cancellation">
                <div className="shipping-guide-warning"><strong>Importante:</strong> MovilesPay no elimina la GRE aceptada. XML, ZIP, CDR, correlativo y respuesta SUNAT permanecen intactos para auditoría.</div>
                <div className="guide-detail-kpis"><article><span>Guía</span><strong>{guide.guide_number}</strong></article><article><span>Traslado</span><strong>{guide.transfer_number}</strong></article></div>
                {step === "REQUEST" ? <>
                    <label><span>Motivo de baja</span><textarea maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej.: GRE emitida por error; el equipo no salió del almacén de origen." /></label>
                    <p className="guide-cancellation-help">Al continuar, la solicitud queda pendiente. Realiza la baja correspondiente en SUNAT SOL antes de confirmarla en MovilesPay.</p>
                </> : <>
                    <div className="guide-cancellation-step-ok"><strong>Solicitud registrada</strong><span>La reversión interna todavía NO se ha ejecutado.</span></div>
                    <label><span>Constancia / referencia SUNAT</span><input maxLength={250} value={sunatReference} onChange={(e) => setSunatReference(e.target.value)} placeholder="Número, constancia o referencia de la baja" /></label>
                    <label><span>Observación de confirmación</span><textarea maxLength={500} value={confirmationNote} onChange={(e) => setConfirmationNote(e.target.value)} placeholder="Opcional" /></label>
                    <div className="shipping-guide-warning"><strong>¿SUNAT no permite registrar la baja?</strong> Si la mercadería nunca salió físicamente del origen, puedes cancelar únicamente el movimiento interno y liberar la reserva de stock. La GRE aceptada y sus archivos SUNAT se conservarán sin alteraciones y la baja tributaria quedará pendiente.</div>
                    <label><span>Incidencia / sustento de reversión logística</span><textarea maxLength={1000} value={incidentNote} onChange={(e) => setIncidentNote(e.target.value)} placeholder="Ej.: SUNAT SOL muestra GRE inconsistente. El equipo nunca salió físicamente de Almacén Central Pucallpa." /></label>
                    <label className="guide-cancellation-physical-confirm"><input type="checkbox" checked={physicalMovementConfirmed} onChange={(e) => setPhysicalMovementConfirmed(e.target.checked)} /><span>Confirmo que la mercadería nunca salió físicamente del almacén de origen.</span></label>
                    <button type="button" className="modal-button modal-button-secondary" disabled={saving || !physicalMovementConfirmed || incidentNote.trim().length < 10} onClick={reverseWithoutPhysicalMovement}>Revertir traslado sin movimiento físico</button>
                </>}
            </div>
            <footer className="inventory-modal-footer"><button type="button" className="modal-button modal-button-secondary" disabled={saving} onClick={close}>Cerrar</button><button type="button" className="modal-button guide-cancel-btn" disabled={saving} onClick={step === "REQUEST" ? request : confirm}>{saving ? "Procesando..." : step === "REQUEST" ? "Registrar solicitud" : "Confirmar baja y cancelar traslado"}</button></footer>
        </section>
    </div>;
}

function ValidationModal({ value, close }) {
    return (
        <div className="inventory-modal shipping-guide-validation-modal">
            <div className="backdrop" onClick={close} />
            <section>
                <header><div><small>TRAZABILIDAD SUNAT</small><h2>Validación {value.fullNumber}</h2></div><button type="button" onClick={close}>×</button></header>
                <div className="shipping-guide-validation">
                    <div className="guide-detail-kpis"><article><span>Preflight</span><strong>{value.preflightStatus}</strong></article><article><span>Estado SUNAT</span><strong>{statusText(value.sunatStatus)}</strong></article><article><span>Ticket</span><strong>{value.ticket || "—"}</strong></article><article><span>Código</span><strong>{value.responseCode || "—"}</strong></article></div>
                    <div className="validation-message"><strong>Respuesta SUNAT</strong><p>{value.responseDescription || "Todavía no existe respuesta SUNAT."}</p></div>
                    {(value.observations || []).length > 0 && <div className="validation-message warning"><strong>Observaciones</strong><ul>{value.observations.map((obs, index) => <li key={`${obs.code || "obs"}-${index}`}>{obs.code ? `${obs.code}: ` : ""}{obs.message || obs.raw}</li>)}</ul></div>}
                    {value.preflight && value.preflight.valid === false && (
                        <div className="validation-message warning">
                            <strong>Incidencias de prevalidación</strong>
                            <ul>
                                {(Array.isArray(value.preflight.details) && value.preflight.details.length
                                    ? value.preflight.details
                                    : [value.preflight.error || "La GRE no superó la prevalidación."]
                                ).map((message, index) => <li key={`preflight-${index}`}>{message}</li>)}
                            </ul>
                            {value.preflight.code && <p><b>Código técnico:</b> {value.preflight.code}</p>}
                        </div>
                    )}
                    <div className="validation-message"><strong>Prevalidación almacenada</strong><pre>{JSON.stringify(value.preflight || {}, null, 2)}</pre></div>
                    <div className="validation-message"><strong>Últimos eventos</strong><div className="validation-attempts">{(value.attempts || []).map((attempt) => <div key={attempt.id}><b>{attempt.action} · {attempt.status}</b><span>{formatDateTime12h(attempt.created_at)}</span><p>{attempt.message}</p></div>)}</div></div>
                </div>
                <footer className="inventory-modal-footer"><button type="button" className="modal-button modal-button-secondary" onClick={close}>Cerrar</button></footer>
            </section>
        </div>
    );
}

function GreOAuthModal({ close }) {
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState(null);
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        inventoryApi.greConfiguration()
            .then((response) => {
                const data = apiPayload(response);
                setRows(Array.isArray(data) ? data : []);
            })
            .catch((error) =>
                toast.error(
                    error.response?.data?.error ||
                        "No se pudo cargar la configuración GRE.",
                ),
            );
    }, []);

    const options = (Array.isArray(rows) ? rows : []).map((row) => ({
        value: Number(row.id),
        label: `${row.business_name} · ${row.ruc}`,
        raw: row,
    }));
    const select = (item) => {
        setSelected(item?.raw || null);
        setClientId(item?.raw?.gre_client_id || "");
        setClientSecret("");
    };
    const save = async () => {
        if (!selected) return toast.error("Selecciona un RUC.");
        setSaving(true);
        try {
            await inventoryApi.saveGreConfiguration(selected.id, { clientId, clientSecret });
            toast.success("OAuth GRE guardado con client_secret cifrado.");
            const next = apiPayload(await inventoryApi.greConfiguration());
            const normalizedRows = Array.isArray(next) ? next : [];
            setRows(normalizedRows);
            setSelected(
                normalizedRows.find(
                    (row) => Number(row.id) === Number(selected.id),
                ) || null,
            );
            setClientSecret("");
        } catch (error) {
            toast.error(error.response?.data?.error || "No se pudo guardar OAuth GRE.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="inventory-modal shipping-guide-oauth-modal">
            <div className="backdrop" onClick={close} />
            <section>
                <header><div><small>SEGURIDAD SUNAT</small><h2>Credenciales OAuth para GRE</h2></div><button type="button" onClick={close}>×</button></header>
                <div className="shipping-guide-form">
                    <GuideField wide label="RUC emisor"><Select styles={selectStyles} menuPortalTarget={document.body} options={options} value={options.find((item) => item.value === Number(selected?.id)) || null} onChange={select} placeholder="Selecciona empresa/RUC" /></GuideField>
                    {selected && <>
                        <GuideField wide label="Estado técnico"><div className="oauth-status-grid"><span>SOL: {selected.has_sol_user && selected.has_sol_password ? "✓" : "Falta"}</span><span>Certificado: {selected.has_certificate ? "✓" : "Falta"}</span><span>OAuth GRE: {selected.gre_client_id && selected.has_gre_client_secret ? "✓" : "Falta"}</span><span>Ambiente: {selected.environment || "—"}</span></div></GuideField>
                        <GuideField wide label="client_id"><input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Client ID generado en SUNAT Operaciones en Línea" /></GuideField>
                        <GuideField wide label="client_secret" hint={selected.has_gre_client_secret ? "Déjalo vacío para conservar el secreto cifrado actual." : "Obligatorio en la primera configuración."}><input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} autoComplete="new-password" /></GuideField>
                        <div className="shipping-guide-warning wide">El client_secret se cifra con la misma clave <b>ACCOUNTING_SECRETS_KEY</b> usada por tus credenciales tributarias. Nunca se devuelve al navegador.</div>
                    </>}
                </div>
                <footer className="inventory-modal-footer"><button type="button" className="modal-button modal-button-secondary" onClick={close}>Cerrar</button><button type="button" className="modal-button modal-button-primary" disabled={!selected || saving} onClick={save}>{saving ? "Guardando..." : "Guardar OAuth GRE"}</button></footer>
            </section>
        </div>
    );
}
